import { useState, useEffect } from 'react';
import { 
  Coins, 
  Users, 
  Search, 
  RefreshCw, 
  Download,
  Plus,
  Minus,
  Filter,
  History,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import { toast } from 'sonner';
import { 
  getAdminOverview, 
  getAdminLedger, 
  adminAdjustCredits,
  adminExportUserCredits,
  getTransactionTypeLabel,
  getSourceLabel,
  formatCreditAmount
} from '../../services/creditService';

export const AdminCreditsPanel = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [overviewData, setOverviewData] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [ledgerPage, setLedgerPage] = useState(1);
  const pageSize = 20;

  // Adjustment dialog state
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustDescription, setAdjustDescription] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  // Ledger filters
  const [ledgerFilters, setLedgerFilters] = useState({
    user_email: '',
    transaction_type: '',
    source: ''
  });

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const data = await getAdminOverview(page, pageSize, searchTerm || null);
      setOverviewData(data);
    } catch (error) {
      console.error('Error fetching overview:', error);
      toast.error('Fehler beim Laden der Credits-Übersicht');
    } finally {
      setLoading(false);
    }
  };

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const filters = {
        page: ledgerPage,
        page_size: pageSize,
        ...Object.fromEntries(
          Object.entries(ledgerFilters).filter(([_, v]) => v !== '')
        )
      };
      const data = await getAdminLedger(filters);
      setLedgerData(data);
    } catch (error) {
      console.error('Error fetching ledger:', error);
      toast.error('Fehler beim Laden des Ledgers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchOverview();
    } else {
      fetchLedger();
    }
  }, [activeTab, page, ledgerPage, searchTerm]);

  const handleAdjustCredits = async () => {
    if (!selectedUser || !adjustAmount || !adjustReason) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    setAdjusting(true);
    try {
      const amount = parseFloat(adjustAmount);
      if (isNaN(amount)) {
        toast.error('Ungültiger Betrag');
        return;
      }

      await adminAdjustCredits(
        selectedUser.user_email,
        amount,
        adjustReason,
        adjustDescription || null
      );

      toast.success(`Credits für ${selectedUser.user_email} erfolgreich angepasst`);
      setAdjustDialogOpen(false);
      setSelectedUser(null);
      setAdjustAmount('');
      setAdjustReason('');
      setAdjustDescription('');
      fetchOverview();
    } catch (error) {
      console.error('Adjustment error:', error);
      toast.error(error.response?.data?.detail || 'Fehler bei der Anpassung');
    } finally {
      setAdjusting(false);
    }
  };

  const handleExportUser = async (email) => {
    try {
      const data = await adminExportUserCredits(email);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `credits-${email.replace('@', '_')}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Export heruntergeladen');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Fehler beim Export');
    }
  };

  const openAdjustDialog = (user) => {
    setSelectedUser(user);
    setAdjustDialogOpen(true);
  };

  const getAmountColor = (amount) => {
    if (amount > 0) return 'text-green-600';
    if (amount < 0) return 'text-red-600';
    return '';
  };

  const overviewTotalPages = overviewData ? Math.ceil(overviewData.total_users / pageSize) : 1;
  const ledgerTotalPages = ledgerData ? Math.ceil(ledgerData.total_transactions / pageSize) : 1;

  return (
    <div className="space-y-6" data-testid="admin-credits-panel">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Credits im System</CardTitle>
            <Coins className="h-4 w-4" style={{ color: 'var(--color-primary)' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overviewData?.total_credits_in_system?.toFixed(2) || '0.00'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Benutzer mit Credits</CardTitle>
            <Users className="h-4 w-4" style={{ color: 'var(--color-text-secondary)' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overviewData?.total_users || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Transaktionen gesamt</CardTitle>
            <History className="h-4 w-4" style={{ color: 'var(--color-text-secondary)' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {ledgerData?.total_transactions || '-'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2" style={{ maxWidth: '400px' }}>
          <TabsTrigger value="overview" data-testid="tab-overview">
            <Users className="w-4 h-4 mr-2" />
            Benutzerübersicht
          </TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">
            <History className="w-4 h-4 mr-2" />
            Audit-Ledger
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Search */}
          <div className="flex gap-4">
            <div className="flex-1 max-w-sm">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                <Input
                  placeholder="Nach E-Mail suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-users-input"
                />
              </div>
            </div>
            <Button variant="outline" onClick={fetchOverview} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>

          {/* Users Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Benutzer</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Guthaben</TableHead>
                    <TableHead className="text-right">Gutschriften</TableHead>
                    <TableHead className="text-right">Verbrauch</TableHead>
                    <TableHead className="text-right">Transaktionen</TableHead>
                    <TableHead>Letzte Aktivität</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : overviewData?.users?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8" style={{ color: 'var(--color-text-secondary)' }}>
                        Keine Benutzer mit Credits gefunden
                      </TableCell>
                    </TableRow>
                  ) : (
                    overviewData?.users?.map((user) => (
                      <TableRow key={user.user_email} data-testid={`user-row-${user.user_email}`}>
                        <TableCell className="font-medium">{user.user_email}</TableCell>
                        <TableCell>{user.full_name || '-'}</TableCell>
                        <TableCell className="text-right font-mono font-bold" style={{ color: 'var(--color-primary)' }}>
                          {user.current_balance?.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-600">
                          +{user.total_credited?.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-600">
                          -{user.total_debited?.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">{user.transaction_count}</TableCell>
                        <TableCell>
                          {user.last_transaction_at ? (
                            new Date(user.last_transaction_at).toLocaleDateString('de-DE')
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAdjustDialog(user)}
                              data-testid={`adjust-btn-${user.user_email}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleExportUser(user.user_email)}
                              data-testid={`export-btn-${user.user_email}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Pagination */}
          {overviewTotalPages > 1 && (
            <div className="flex justify-between items-center">
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Seite {page} von {overviewTotalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Zurück
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(overviewTotalPages, p + 1))}
                  disabled={page === overviewTotalPages}
                >
                  Weiter
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Ledger Tab */}
        <TabsContent value="ledger" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px] max-w-sm">
              <Input
                placeholder="E-Mail filtern..."
                value={ledgerFilters.user_email}
                onChange={(e) => setLedgerFilters(f => ({ ...f, user_email: e.target.value }))}
                data-testid="filter-email-input"
              />
            </div>
            <Select
              value={ledgerFilters.transaction_type}
              onValueChange={(value) => setLedgerFilters(f => ({ ...f, transaction_type: value === 'all' ? '' : value }))}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-type-select">
                <SelectValue placeholder="Transaktionstyp" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Typen</SelectItem>
                <SelectItem value="credit">Gutschrift</SelectItem>
                <SelectItem value="debit">Verbrauch</SelectItem>
                <SelectItem value="refund">Rückbuchung</SelectItem>
                <SelectItem value="adjustment">Anpassung</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={ledgerFilters.source}
              onValueChange={(value) => setLedgerFilters(f => ({ ...f, source: value === 'all' ? '' : value }))}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-source-select">
                <SelectValue placeholder="Quelle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Quellen</SelectItem>
                <SelectItem value="zoho_invoice">Zoho Rechnung</SelectItem>
                <SelectItem value="zoho_payment">Zoho Zahlung</SelectItem>
                <SelectItem value="manual">Manuell</SelectItem>
                <SelectItem value="external_app">Externe App</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchLedger} disabled={loading}>
              <Filter className="w-4 h-4 mr-2" />
              Filtern
            </Button>
          </div>

          {/* Ledger Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Benutzer</TableHead>
                    <TableHead>Typ</TableHead>
                    <TableHead>Grund</TableHead>
                    <TableHead>Quelle</TableHead>
                    <TableHead className="text-right">Betrag</TableHead>
                    <TableHead className="text-right">Saldo danach</TableHead>
                    <TableHead>Erstellt von</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : ledgerData?.transactions?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8" style={{ color: 'var(--color-text-secondary)' }}>
                        Keine Transaktionen gefunden
                      </TableCell>
                    </TableRow>
                  ) : (
                    ledgerData?.transactions?.map((tx) => (
                      <TableRow key={tx.ledger_id} data-testid={`ledger-row-${tx.ledger_id}`}>
                        <TableCell className="font-medium whitespace-nowrap">
                          {new Date(tx.created_at).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell>{tx.user_email || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={tx.amount >= 0 ? 'default' : 'secondary'}>
                            {getTransactionTypeLabel(tx.transaction_type)}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">{tx.reason}</TableCell>
                        <TableCell>
                          <span className="text-xs">{getSourceLabel(tx.source)}</span>
                        </TableCell>
                        <TableCell className={`text-right font-mono font-bold ${getAmountColor(tx.amount)}`}>
                          {formatCreditAmount(tx.amount)}
                        </TableCell>
                        <TableCell className="text-right font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                          {tx.balance_after?.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          {tx.created_by || 'System'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Ledger Pagination */}
          {ledgerTotalPages > 1 && (
            <div className="flex justify-between items-center">
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Seite {ledgerPage} von {ledgerTotalPages} ({ledgerData?.total_transactions} Einträge)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLedgerPage(p => Math.max(1, p - 1))}
                  disabled={ledgerPage === 1}
                >
                  Zurück
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLedgerPage(p => Math.min(ledgerTotalPages, p + 1))}
                  disabled={ledgerPage === ledgerTotalPages}
                >
                  Weiter
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Adjust Credits Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent data-testid="adjust-credits-dialog">
          <DialogHeader>
            <DialogTitle>Credits anpassen</DialogTitle>
            <DialogDescription>
              Passen Sie die Credits für {selectedUser?.user_email} an.
              Positive Werte = Gutschrift, Negative Werte = Abbuchung
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Benutzer</Label>
              <Input value={selectedUser?.user_email || ''} disabled />
            </div>

            <div className="space-y-2">
              <Label>Aktuelles Guthaben</Label>
              <Input value={`${selectedUser?.current_balance?.toFixed(2) || '0.00'} Credits`} disabled />
            </div>

            <div className="space-y-2">
              <Label htmlFor="adjustAmount">Betrag *</Label>
              <Input
                id="adjustAmount"
                type="number"
                step="0.01"
                placeholder="z.B. 50 oder -25"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                data-testid="adjust-amount-input"
              />
              <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                Positiv für Gutschrift, negativ für Abbuchung
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adjustReason">Grund *</Label>
              <Input
                id="adjustReason"
                placeholder="z.B. Korrektur, Bonusgutschrift"
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                data-testid="adjust-reason-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="adjustDescription">Beschreibung (optional)</Label>
              <Textarea
                id="adjustDescription"
                placeholder="Zusätzliche Details..."
                value={adjustDescription}
                onChange={(e) => setAdjustDescription(e.target.value)}
                data-testid="adjust-description-input"
              />
            </div>

            {adjustAmount && !isNaN(parseFloat(adjustAmount)) && (
              <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
                  <span className="text-sm font-medium">Vorschau:</span>
                </div>
                <p className="text-sm mt-1">
                  Neues Guthaben: {' '}
                  <span className="font-bold">
                    {((selectedUser?.current_balance || 0) + parseFloat(adjustAmount)).toFixed(2)} Credits
                  </span>
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleAdjustCredits}
              disabled={adjusting || !adjustAmount || !adjustReason}
              data-testid="confirm-adjust-btn"
            >
              {adjusting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Wird gespeichert...
                </>
              ) : (
                'Credits anpassen'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCreditsPanel;
